<template lang="pug">
div
  v-card.round-xl.pa-4(outlined)
    //- profile section
    v-row(dense)
      v-col.shrink
        v-img.round-xl(v-if="patient.profile_image" width="60" height="80" :src="$options.filters.mediumImage(patient.profile_image.file)" cover)
          template(v-slot:placeholder)
            v-row(class="fill-height ma-0" align="center" justify="center")
              v-progress-circular(indeterminate color="grey")
        v-card.neutral300.fill-height.round-xl(v-else flat width="60" height="80")
          div.d-flex.align-center.justify-center.fill-height
            v-icon(size="30") $yzi-user-03
      v-col
        div.text-sm.font-medium.neutral700--text {{ patient.name }}
        div.text-xs.neutral500--text(v-if="patient.dob") {{ $options.filters.getAgeByYearMonthDay(patient.dob) | displayAgeByYearMonthDay }} old
        div.text-xs.neutral500--text.mt-1 {{ patient.ic }}
      v-col.shrink
        div.d-flex.align-center
          y-button.mr-2(label="View profile" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="openProfileWindow")
          y-button(label="Edit" size="xxs" height="24" unset-min-width button-class="px-2" @click="editPatient")
    //- family relation section
    div.mt-3
      v-card.text-sm.round-xl.border-menu.pa-3(flat color="neutral200" :loading="isFetchingFamily")
        div.d-flex.align-center.fill-height
          v-icon.mr-2(size="16") $yzi-users-02
          .text-sm(v-if="hasFamilyMember") {{ patient.relationships.length }} family {{ 'member' | pluralize({ count:patient.relationships.length }) }}
          .text-sm(v-else) No family member
          v-spacer
          y-button(v-if="hasFamilyMember" label="See details" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" size="xxs" height="24" outlined unset-min-width button-class="px-2" @click="handleClickFamilyRelation")
          y-button(v-else label="Add now" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" size="xxs" height="24" outlined unset-min-width button-class="px-2" @click="handleClickFamilyRelation")
    //- allergies & conditions section
    div.mt-3
      v-card.text-sm.round-xl.border-menu.pa-3(flat color="neutral200")
        v-row(no-gutters style="gap: 12px;")
          //- underlying conditions
          v-col
            div.text-xs.font-medium.neutral700--text(style="height: 24px; align-content: center;") Underlying conditions
            template(v-if="!patientRecord.underlying_conditions || !patientRecord.underlying_conditions.length")
              div.text-xxs.italic.neutral500--text No underlying conditions
            yz-truncated-list.mt-1(v-else :items="patientRecord.underlying_conditions" :gap="8")
              template(#visible-item="{ item }")
                v-tooltip(top color="black" attach content-class="black tooltip-top-arrow")
                  template(#activator="{ on, attrs }")
                    div.clickable(v-bind="attrs" v-on="on")
                      y-chip(:label="item.name" :width="100" outlined)
                        template(#text)
                          span.ellipsis-text-1
                            span.text-xxs.font-medium.neutral700--text {{ item.name }}
                            span.text-xxs.italic.neutral500--text.ml-1 {{ item.remarks }}
                  div(style="max-width: 30vw")
                    div.text-xs.font-medium {{ item.name }}
                    div.text-xxs.italic {{ item.remarks }}
              template(#hidden-item-tooltip="{ item }")
                li
                  div.text-xs.font-medium {{ item.name }}
                  div.text-xxs.italic {{ item.remarks }}
          //- allergies
          v-col
            div.d-flex.align-center(style="height: 24px")
              div.text-xs.font-medium.neutral700--text Allergies
              v-spacer
              y-button(label="Edit" outlined color="neutral400" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="showAllergiesConditionDialog = true")
            template(v-if="!patientRecord.allergies || !patientRecord.allergies.length")
              div.text-xxs.italic.neutral500--text No allergies
            yz-truncated-list.mt-1(v-else :items="patientRecord.allergies" :gap="8")
              template(#visible-item="{ item }")
                v-tooltip(top color="black" attach content-class="black tooltip-top-arrow")
                  template(#activator="{ on, attrs }")
                    div.clickable(v-bind="attrs" v-on="on")
                      y-chip(:label="item.name" :width="100" outlined)
                        template(#text)
                          span.ellipsis-text-1
                            span.text-xxs.font-medium.neutral700--text {{ item.name }}
                            span.text-xxs.italic.neutral500--text.ml-1 {{ item.reactions }}
                  div(style="max-width: 30vw")
                    div.text-xs.font-medium {{ item.name }}
                    div.text-xxs.italic {{ item.reactions }}
              template(#hidden-item-tooltip="{ item }")
                li
                  div.text-xs.font-medium {{ item.name }}
                  div.text-xxs.italic {{ item.reactions }}
  //- outstanding invoice card
  v-card.round-xl.mt-2(v-if="showOutstandingBills && outstandingInvoice" outlined :loading="isFetchingOutstandingBills")
    div.pa-4
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral500--text Outstanding bill
        v-spacer
        y-button(label="View all" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="showOutstandingBillsDialog = true")
      v-row.mt-0
        v-col(cols="6")
          div.text-xs.neutral700--text Number of invoices
          div.text-g6.neutral700--text.mt-1 {{ outstandingInvoice.count }}
        v-divider(vertical)
        v-col(cols="6")
          div.text-xs.neutral700--text Total outstanding
          div.text-g6.yellow600--text.mt-1 {{ outstandingInvoice.amount | displayPrice($currency) }}
  //- appointment card
  v-card.round-xl.mt-2(outlined :loading="isFetchingAppt")
    div.pa-4(v-if="Auth('component', '/admin/clinic/consultation#set_appointment')")
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral500--text Upcoming appointment
        v-spacer
        y-button(label="View all" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="showUpcomingAppointmentDialog = true")
        v-divider.neutral400.mx-2(vertical)
        y-button(label="Add new" size="xxs" height="24" unset-min-width button-class="px-2" @click="openSetAppointmentDialog")
      v-row.mt-3(v-if="appointment" no-gutters)
        v-col(cols="3")
          div.text-g9.neutral700--text {{ appointment.start | humanizeDate }}
        v-col(cols="9")
          v-alert.clickable.my-0.py-0(border="left" colored-border dense color="blue500" style="background-color: var(--v-neutral100);" @click="showAppointmentDetailsDialog = true")
            div.text-sm.font-medium.neutral700--text {{ getAppointmentProviderServices(appointment) }}
            div.text-xs.neutral500--text(v-if="appointment.time_range && appointment.time_range.length") {{ appointment.time_range[0] | displayTime2 }} - {{ appointment.time_range[1] | displayTime2 }} 
      div.text-xs.font-medium.neutral500--text.text-center.mt-6(v-else) No upcoming appointment
    div.pa-4(v-else)
      div.d-flex.align-center
        div
          div.d-flex.align-center
            div.text-xs.font-semibold.neutral500--text Upcoming appointment
            y-chip.font-medium.ml-3(label="Appointment" size="xxs" chip-class="pa-1" color="blue" text-color="blue500")
              template(#prepend)
                v-icon.mr-1(color="blue500" size="10") $yzi-zap
          div.text-sm.neutral700--text.mt-3 Set follow up appointments faster better cheaper.
        v-spacer
        div.pl-3
          y-button(label="Upgrade" color="neutral300" size="xxs" height="24" unset-min-width button-class="px-2" @click="openUpsellFeatureDialog('appointment', null)")
  //- marketing card
  template(v-if="Auth('root', 'subscribe_marketing')")
    checkout-offers-card.mt-2(:patient="patient" :disabled="true" disable-text="Offers can be applied during the dispensary checkout process")
    checkout-loyalty-card.mt-2
  template(v-else)
    template(v-for="n in 2")
      v-card.round-xl.mt-2(outlined)
        div.pa-4
          div.d-flex.align-center
            div
              div.d-flex.align-center
                div.text-xs.font-semibold.neutral500--text Offers
                y-chip.font-medium.ml-3(label="Marketing" size="xxs" chip-class="pa-1" color="blue" text-color="blue500")
                  template(#prepend)
                    v-icon.mr-1(color="blue500" size="10") $yzi-zap
              div.text-sm.neutral700--text.mt-3(v-if="n == 1") Give exciting offers and discounts to your customers to improve their experience
              div.text-sm.neutral700--text.mt-3(v-if="n == 2") Enrol customer to your loyalty program to ensure revisits
            v-spacer
            div.pl-3
              y-button(label="Upgrade" color="neutral300" size="xxs" height="24" unset-min-width button-class="px-2" @click="openUpsellFeatureDialog('marketing', null)")

  .dialogs
    add-edit-family-dialog(v-if="showAddEditFamilyDialog" :open.sync="showAddEditFamilyDialog" :patientId="patient.id" :patientName="patient.name" @saved="handleFamilyUpdated")
    family-relation-dialog(v-if="showFamilyRelationDialog" :open.sync="showFamilyRelationDialog" :patientId="patient.id" @updated="handleFamilyUpdated")
    allergies-conditions-dialog(v-if="showAllergiesConditionDialog" :open.sync="showAllergiesConditionDialog" :patientId="patient && patient.id" :allergies="patientRecord.allergies" :underlying-conditions="patientRecord.underlying_conditions" @updated="handleAllergiesConditions")
    outstanding-bill-dialog(v-if="showOutstandingBillsDialog" :open.sync="showOutstandingBillsDialog" :invoices="outstandingInvoice.results" @selected="OpenOutstandingInvoice($event)")
    upcoming-appointment-dialog(v-if="showUpcomingAppointmentDialog" :open.sync="showUpcomingAppointmentDialog" @closed="fetchAppointments")
    set-appointment-dialog(v-if="showSetAppointmentDialog" :open.sync="showSetAppointmentDialog" :patient="patient" @advance="openAdvanceAppointmentDialog" @closed="fetchAppointments")
    add-advance-appointment(v-if="showAdvAppointmentDialog" :open.sync="showAdvAppointmentDialog" :patient="patient" @reset="fetchAppointments")
    view-appointment-details(v-if="showAppointmentDetailsDialog" :open.sync="showAppointmentDetailsDialog" :selected-appointment="appointment" :no-action="true")
    upsell-feature-dialog(v-if="showUpsellFeatureDialog" :open.sync="showUpsellFeatureDialog" :feature="selectedFeature" :link="featureLink" @closed="closeUpsellFeatureDialog")
    y-dialog(v-if="showOutstandingInvoice" v-model="showOutstandingInvoice" max-width="500" persistent fluid scrollable content-class="round-2xl")
      v-card
        v-card-text.pa-6
          div.d-flex.align-center
            div.text-xl.font-medium.neutral700--text Open invoice {{ `#${selectedOutstandingInvoice.rid}` }}
            v-spacer
            v-btn(text icon @click="closeOutstandingInvoice")
              v-icon(color="neutral700" :size="24") mdi-close
          div.text-md.neutral700--text.mt-4 You will access another invoice. Would you like to open it in this tab, or would you prefer to open a new tab instead?
        v-divider
        v-card-actions.pa-6
          v-row
            v-col
              y-button(label="This tab" color="neutral700" outlined size="md" block @click="openInvoiceThisTab")
            v-col
              y-button(label="New tab" text-color="neutral100" color="neutral700" size="md" block dark @click="openInvoiceNewTab")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddEditFamilyDialog from '@/components/clinic/relationship/AddEditFamilyDialog.vue'
import FamilyRelationDialog from '@/components/clinic/relationship/FamilyRelationDialog.vue'
import AllergiesConditionsDialog from '@/components/clinic/consultation/AllergiesConditionsDialog.vue'
import OutstandingBillDialog from '@/components/clinic/checkout/OutstandingBillDialog.vue'
import UpcomingAppointmentDialog from '@/components/clinic/checkout/UpcomingAppointmentDialog.vue'
import SetAppointmentDialog from '@/components/clinic/dialogs/SetAppointmentDialog.vue'
import AddAdvanceAppointment from '@/components/clinic/dialogs/AddAdvanceAppointment.vue'
import ViewAppointmentDetails from '@/components/clinic/dialogs/ViewAppointmentDetails.vue'
import CheckoutOffersCard from '@/components/clinic/checkout/marketing/CheckoutOffersCard.vue'
import CheckoutLoyaltyCard from '@/components/clinic/checkout/marketing/CheckoutLoyaltyCard.vue'
import UpsellFeatureDialog from '@/components/clinic/registration/UpsellFeatureDialog.vue'

export default {
	components: { AddEditFamilyDialog, FamilyRelationDialog, AllergiesConditionsDialog, OutstandingBillDialog, UpcomingAppointmentDialog, SetAppointmentDialog, AddAdvanceAppointment, ViewAppointmentDetails, CheckoutOffersCard, CheckoutLoyaltyCard, UpsellFeatureDialog },
	props: {
		patient: {
			type: Object,
			required: true
		},
	},
	data() {
		return {
			isFetchingFamily: false,
			isFetchingOutstandingBills: false,
			isFetchingAppt: false,
			showAddEditFamilyDialog: false,
      showFamilyRelationDialog: false,
      showAllergiesConditionDialog: false,
			showOutstandingBillsDialog: false,
			showUpcomingAppointmentDialog: false,
			showSetAppointmentDialog: false,
			showAdvAppointmentDialog: false,
			showAppointmentDetailsDialog: false,
			showOutstandingInvoice: false,
			showUpsellFeatureDialog: false,
			selectedFeature: null,
			featureLink: null,
			selectedOutstandingInvoice: null,
			outstandingInvoice: null,
			appointment: null,
		};
	},
	computed: {
		...mapGetters({
      company: 'getCompany',
    }),
		hasFamilyMember() {
			return this.patient.relationships && this.patient.relationships.length > 0;
		},
		patientRecord() {
			let record = {
				allergies: [],
				underlying_conditions: []
			}
			if (this.patient.record) {
				record.allergies = this.patient.record.allergies || [];
				record.underlying_conditions = this.patient.record.underlying_conditions || [];
			}
			return record
		},
		showOutstandingBills () {
      // Always show outstanding bills for new checkout registration
      return true
    },
	},
	mounted() {
		this.fetchOutstandingInvoices()
		this.fetchAppointments()
	},
	methods: {
		...mapActions(['showMessage']),
		fetchOutstandingInvoices () {
			this.isFetchingOutstandingBills = true
      let params = {
        company: this.company.id,
        paid: false,
        form_type: 'registration',
        customer: this.patient.id,
        page_size: -1
      }
      this.$axios.get(`/invoices`, { params })
        .then(resp => {
          if (resp.data && resp.data.results.length > 0) {
            const results = resp.data.results
            if (results.length > 0) {
              this.outstandingInvoice = {
                id: this.patient.id,
                amount: results.reduce((sum, a) => { return sum + (a.total_amount - a.total_paid) }, 0),
                count: results.length,
                results,
              }
            }
          }
        })
        .catch(() => {})
				.finally(() => {
					this.isFetchingOutstandingBills = false
				})
    },
		fetchAppointments () {
      this.isFetchingAppt = true
      let params = {
        page_size: 1,
        patient: this.patient.id,
        statuses: 'new,processed',
        ordering: 'start',
      }
      this.$axios.get(`/clinics/${this.company.slug}/appointments`, { params })
        .then(resp => {
          if (resp.data.results.length > 0) {
            this.appointment = resp.data.results[0]
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingAppt = false
        })
    },
    getAppointmentProviderServices (appointment) {
      let data = []
      if (appointment.provider && appointment.provider.name) {
        data.push(appointment.provider.name)
      }
      if (appointment.services.length) {
        data = [...data, ...appointment.services.map(s => s.name)]
      }
      return data.join(', ')
    },
		handleClickFamilyRelation () {
      if (this.hasFamilyMember) {
        this.showFamilyRelationDialog = true
      } else {
        this.showAddEditFamilyDialog = true
      }
    },
		handleFamilyUpdated () {
      this.isFetchingFamily = true
      this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patient.id}`, { params: { fields: 'relationships' } })
        .then(resp => {
          this.$set(this.patient, 'relationships', resp.data.relationships)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingFamily = false
        })
    },
		handleAllergiesConditions(data) {
      this.$store.commit('treatment/updateConsultation', {
        allergies: data.allergies,
        underlying_conditions: data.underlying_conditions
      })
    },
		openProfileWindow() {
      window.open(`/admin/clinic/patient/${this.patient.id}`, '_blank')
    },
		openInvoiceThisTab () {
      this.$router.push(`/admin/clinic/checkout/${this.selectedOutstandingInvoice.pid}`)
      this.$router.go()
      this.closeOutstandingInvoice()
    },
    openInvoiceNewTab () {
      const link = this.$router.resolve(`/admin/clinic/checkout/${this.selectedOutstandingInvoice.pid}`)
      window.open(link.href, '_blank')
      this.closeOutstandingInvoice()
    },
    OpenOutstandingInvoice (invoice) {
      this.selectedOutstandingInvoice = invoice
      this.showOutstandingInvoice = true
    },
    openSetAppointmentDialog () {
      this.showSetAppointmentDialog = true
    },
    openAdvanceAppointmentDialog () {
      this.showSetAppointmentDialog = false
      this.showAdvAppointmentDialog = true
    },
    openUpsellFeatureDialog (feature, link) {
      this.selectedFeature = feature
      this.featureLink = link
      this.showUpsellFeatureDialog = true
    },
    closeOutstandingInvoice () {
      this.showOutstandingInvoice = false
      this.selectedOutstandingInvoice = null
    },
    closeUpsellFeatureDialog () {
      this.showUpsellFeatureDialog = false
      this.selectedFeature = null
      this.featureLink = null
    },
		editPatient () {
      this.$emit('goback')
    },
	},
}
</script>

<style lang="scss" scoped>

</style>